import { Controller } from '@hotwired/stimulus'

export class SupportingDocumentController extends Controller {
  static targets = ['field', 'fileInput', 'label', 'wrapper']
  static classes = ['hidden', 'show']

  declare readonly fieldTarget: HTMLInputElement
  declare readonly fileInputTarget: HTMLInputElement
  declare readonly labelTarget: HTMLElement
  declare readonly wrapperTarget: HTMLElement
  declare readonly hiddenClasses: string[]
  declare readonly showClasses: string[]

  connect() {
    this.fieldTarget.classList.add(...this.hiddenClasses)
    this.labelTarget.classList.remove(...this.hiddenClasses)

    // Initialize the display with the input value if it exists
    if (this.input.value) {
      this.labelTarget.textContent = this.input.value
    }
  }

  get input() {
    return this.fieldTarget.querySelector('input')
  }

  handleFileSelected() {
    const file = this.fileInputTarget.files?.[0]

    if (file) {
      // Update the file name display
      this.labelTarget.textContent = file.name

      // Auto-populate the name field if it's empty
      if (!this.input.value) {
        // Remove file extension from the name
        const fileName = file.name.replace(/\.[^/.]+$/, '')
        this.input.value = fileName
      }

      // Dispatch a custom event to notify the form that a file has been selected
      document.dispatchEvent(
        new CustomEvent('file-selected', {
          bubbles: true,
          detail: { fileName: file.name },
        }),
      )
    } else {
      this.labelTarget.textContent = ''
    }
  }

  // Inline edit functionality
  showEdit() {
    // Hide display, show input
    this.fieldTarget.classList.remove(...this.hiddenClasses)
    this.labelTarget.classList.add(...this.hiddenClasses)
    this.wrapperTarget.classList.remove(...this.showClasses)

    // Set input value to current display text
    if (this.labelTarget.textContent) {
      this.input.value = this.labelTarget.textContent.trim()
    }

    // Focus the input
    this.input.focus()
  }

  hideEdit() {
    // Update display with input value
    if (this.input.value) {
      this.labelTarget.textContent = this.input.value
    }

    // Hide input, show display
    this.fieldTarget.classList.add(...this.hiddenClasses)
    this.labelTarget.classList.remove(...this.hiddenClasses)
    this.wrapperTarget.classList.add(...this.showClasses)
  }

  handleKeydown(event: KeyboardEvent) {
    // Submit on Enter
    if (event.key === 'Enter') {
      event.preventDefault()
      this.hideEdit()
    }

    // Cancel on Escape
    if (event.key === 'Escape') {
      event.preventDefault()
      // Revert to original value
      this.input.value = this.labelTarget.textContent || ''
      this.hideEdit()
    }
  }
}
